<template>
  <div class="card-wrap">
    <template v-if="status !== 'completed' && status !== 'failed'">
      <div class="loader-wrap">
        <i class="el-icon-loading loader-icon"></i>
        <h3 class="title-center">Importando registros...</h3>
        <p class="sub-center">
          No cierres esta ventana. Esto puede tomar unos segundos.
        </p>
        <el-progress
          :percentage="progress"
          :stroke-width="10"
          color="#3ecfb4"
        />
        <div class="progress-info">
          {{ processedRows }} de {{ totalRows || "?" }} procesados
        </div>
      </div>
    </template>

    <template v-else-if="status === 'completed'">
      <div class="result-icon ok">
        <i class="el-icon-check"></i>
      </div>
      <h3 class="title-center">Importación completada</h3>
      <p class="sub-center">Tu {{ moduleName }} fue importado correctamente.</p>

      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-num teal">{{ summary.imported || 0 }}</div>
          <div class="stat-label">Importados</div>
        </div>
        <div class="stat-card">
          <div class="stat-num orange">{{ summary.skipped || 0 }}</div>
          <div class="stat-label">Saltados</div>
        </div>
        <div class="stat-card">
          <div class="stat-num primary">{{ summary.duplicates || 0 }}</div>
          <div class="stat-label">Duplicados</div>
        </div>
        <div class="stat-card">
          <div class="stat-num red">{{ summary.errors || 0 }}</div>
          <div class="stat-label">Errores</div>
        </div>
      </div>

      <div v-if="errorLog && errorLog.length" class="errors-block">
        <el-collapse>
          <el-collapse-item :title="`Ver detalles (${errorLog.length})`">
            <div
              v-for="(err, idx) in errorLog.slice(0, 50)"
              :key="idx"
              class="err-item"
            >
              <strong>Fila {{ err.row }}:</strong>
              {{ err.message }}
            </div>
            <div v-if="errorLog.length > 50" class="err-more">
              ...y {{ errorLog.length - 50 }} más. Descarga el log completo.
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>

      <div v-if="module === 'invoices'" class="info-banner">
        <i class="el-icon-data-line"></i>
        <div>
          Las facturas importadas están disponibles en
          <strong>Reportes</strong> para comparativos de ventas año contra año.
          No interfieren con tus facturas SRI vivas.
        </div>
      </div>

      <div class="btn-row">
        <el-button
          v-if="errorLog && errorLog.length"
          plain
          @click="downloadLog"
        >
          <i class="el-icon-download"></i> Descargar log
        </el-button>
        <el-button plain @click="$emit('restart')">
          Importar otro módulo
        </el-button>
        <el-button type="success" @click="$emit('goto-module')">
          Ir al módulo {{ moduleName }} →
        </el-button>
      </div>
    </template>

    <template v-else>
      <div class="result-icon err">
        <i class="el-icon-close"></i>
      </div>
      <h3 class="title-center">La importación falló</h3>
      <p class="sub-center error-text">
        {{ summary.error || "Ocurrió un error procesando el archivo." }}
      </p>
      <div class="btn-row center">
        <el-button type="primary" @click="$emit('restart')">
          Reintentar
        </el-button>
      </div>
    </template>
  </div>
</template>

<script>
import api from "@/config/Api";

export default {
  name: "StepImporting",
  props: {
    sessionId: { type: [Number, String], required: true },
    module: { type: String, required: true }
  },
  data() {
    return {
      status: "processing",
      progress: 0,
      processedRows: 0,
      totalRows: 0,
      summary: {},
      errorLog: [],
      pollHandle: null
    };
  },
  computed: {
    moduleName() {
      const names = {
        catalog: "Catálogo",
        directory: "Directorio",
        invoices: "Facturas históricas"
      };
      return names[this.module] || "";
    }
  },
  mounted() {
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    startPolling() {
      this.fetchStatus();
      this.pollHandle = setInterval(this.fetchStatus, 1500);
    },
    stopPolling() {
      if (this.pollHandle) {
        clearInterval(this.pollHandle);
        this.pollHandle = null;
      }
    },
    async fetchStatus() {
      try {
        const { data } = await api().get(
          `/integrations/imports/${this.sessionId}/status`
        );
        this.status = data.status || this.status;
        this.progress = data.progress || 0;
        this.processedRows = data.processed_rows || 0;
        this.totalRows = data.total_rows || 0;
        if (data.summary) this.summary = data.summary;
        if (data.error_log) this.errorLog = data.error_log;

        if (this.status === "completed" || this.status === "failed") {
          this.stopPolling();
        }
      } catch (e) {
        // ignore — interceptor notifies; keep polling
      }
    },
    downloadLog() {
      if (!this.errorLog || !this.errorLog.length) return;
      const rows = [["row", "type", "message"]];
      this.errorLog.forEach(e =>
        rows.push([e.row || "", e.type || "error", e.message || ""])
      );
      const csv = rows
        .map(r =>
          r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(",")
        )
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `import-${this.sessionId}-errors.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style lang="scss" scoped>
.card-wrap {
  background: #fff;
  border: 0.5px solid #e4eaf6;
  border-radius: 12px;
  padding: 2.25rem 1.75rem;
  text-align: center;
}

.loader-wrap {
  max-width: 480px;
  margin: 0 auto;
}

.loader-icon {
  font-size: 40px;
  color: #3ecfb4;
  margin-bottom: 1rem;
}

.result-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto 1rem;

  &.ok {
    background: #e1f5ee;
    color: #0f6e56;
  }

  &.err {
    background: #fcebeb;
    color: #e24b4a;
  }
}

.title-center {
  font-size: 18px;
  font-weight: 600;
  color: #2d2b55;
  margin: 0 0 6px;
}

.sub-center {
  font-size: 13px;
  color: #6e7484;
  margin: 0 0 1.5rem;
}

.error-text {
  color: #e24b4a;
}

.progress-info {
  font-size: 12px;
  color: #6e7484;
  margin-top: 8px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin: 1.5rem 0;
  text-align: left;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.stat-card {
  background: #f7f7f5;
  border-radius: 8px;
  padding: 1rem;
}

.stat-num {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 2px;

  &.primary {
    color: #2d2b55;
  }
  &.teal {
    color: #3ecfb4;
  }
  &.orange {
    color: #ef9f27;
  }
  &.red {
    color: #e24b4a;
  }
}

.stat-label {
  font-size: 12px;
  color: #6e7484;
}

.errors-block {
  text-align: left;
  margin: 1rem 0;
}

.err-item {
  font-size: 12px;
  color: #501313;
  padding: 6px 12px;
  border-left: 3px solid #e24b4a;
  background: #fcebeb;
  border-radius: 4px;
  margin-bottom: 6px;
}

.err-more {
  font-size: 12px;
  color: #6e7484;
  font-style: italic;
  padding: 8px 12px;
}

.info-banner {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  background: #e1f5ee;
  border-radius: 8px;
  font-size: 12px;
  color: #0f6e56;
  text-align: left;
  margin: 1rem 0;

  i {
    font-size: 16px;
    margin-top: 1px;
  }
}

.btn-row {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 1.5rem;

  &.center {
    justify-content: center;
  }
}
</style>
